import React, { useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {
  AppBar,
  Card,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  FirstPage,
  LastPage,
  NavigateBefore,
  NavigateNext,
  Search as SearchIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { GET } from "APIclient";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import * as XLSX from "xlsx";
import { formatDesignation, formatDate } from "constants/ApplicationConstants";
import properties from "constants/ApplicationConstants";

const rowLength = 20;

function CustomPagination({
  page,
  pageSize,
  totalRows,
  totalPages,
  onPageChange,
  onGoToLastPage,
  loading,
}) {
  const handleFirstPage = () => onPageChange(0);
  const handlePreviousPage = () => onPageChange(Math.max(0, page - 1));
  const handleNextPage = () => onPageChange(Math.min(totalPages - 1, page + 1));
  const handleLastPage = () => onGoToLastPage();

  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1,
        borderTop: "1px solid #e0e0e0",
      }}
    >
      <Typography variant="caption" sx={{ color: "#666" }}>
        {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : "No rows"}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" sx={{ mr: 2, color: "#666" }}>
          Page {page + 1} of {totalPages || 1}
        </Typography>

        <Tooltip title="First Page">
          <IconButton
            onClick={handleFirstPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <FirstPage />
          </IconButton>
        </Tooltip>

        <Tooltip title="Previous Page">
          <IconButton
            onClick={handlePreviousPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <NavigateBefore />
          </IconButton>
        </Tooltip>

        <Tooltip title="Next Page">
          <IconButton
            onClick={handleNextPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Tooltip>

        <Tooltip title="Last Page">
          <IconButton
            onClick={handleLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || "#1976d2",
              color: "white",
              "&:hover": {
                backgroundColor:
                  properties?.colors?.bgcolorbluethem || "#1565c0",
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e",
              },
            }}
          >
            <LastPage />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default function EmployeeBillingReport() {
  const theme = useTheme();
  const [empId, setEmpId] = useState("");
  const [empName, setEmpName] = useState("");
  const [chargeability, setChargeability] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(rowLength);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const columns = [
    { field: "id", headerName: "#", width: 30, align: "left", headerAlign: "left" },
    { field: "employeeId", headerName: "Employee ID", width: 120 },
    { field: "employeeName", headerName: "Employee Name", width: 200 },
    {
      field: "employeeRoleOrDepartment",
      headerName: "Role/Department",
      width: 180,
    },
    {
      field: "startDate",
      headerName: "Start Date",
      width: 180,
      renderCell: (params) => formatDate(params.value) || " - ",
    },
    {
      field: "endDate",
      headerName: "End Date",
      width: 180,
      renderCell: (params) => formatDate(params.value) || " - ",
    },
    {
      field: "chargeability",
      headerName: "Chargeability",
      width: 120,
      renderCell: (params) => {
        const chargeability = params.value;
        return (
          <MDButton
            variant="contained"
            color={chargeability === 1 ? "success" : "error"}
            size="small"
            disabled
            sx={{
              textTransform: "capitalize",
              fontSize: "0.800rem",
              width: "80px",
              justifyContent: "center",
            }}
          >
            {chargeability === 1 ? "Billable" : "Non-Billable"}
          </MDButton>
        );
      },
    },
  ];

  const fetchData = async (page, pageSize, empId, empName, chargeability, signal) => {
    try {
      setLoading(true);
      const url = `admin/billableNonBillable?offset=${page}&limit=${pageSize}&empId=${empId}&empName=${empName}&chargeability=${chargeability}`;
      const response = await GET(url, { signal });
      const paginatedData = response?.data?.data?.paginatedData || [];
      const totalRecords = response?.data?.data?.totalItems || 0;
      const totalPages = response?.data?.data?.totalPages || 0;

      const transformedData = paginatedData.map((item, index) => ({
        ...item,
        id: page * pageSize + index + 1,
        employeeRoleOrDepartment: formatDesignation(item.employeeRoleOrDepartment),
        endDate: item.endDate || "N/A",
      }));

      setFilteredData(transformedData);
      setTotalRows(totalRecords);
      setTotalPages(totalPages);
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Error fetching billable/non-billable data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchData(page, pageSize, empId, empName, chargeability, controller.signal);
    return () => controller.abort();
  }, [page, pageSize]);

  const handleSearch = () => {
    setPage(0);
    fetchData(0, pageSize, empId, empName, chargeability);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setTimeout(() => window.scrollTo(0, 0), 0);
  };

  const handleGoToLastPage = async () => {
    if (totalPages > 0) {
      const lastPageIndex = totalPages - 1;
      setPage(lastPageIndex);
      await fetchData(lastPageIndex, pageSize, empId, empName, chargeability);
      setTimeout(() => window.scrollTo(0, 0), 0);
    }
  };

  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Billable Non-Billable List");
    XLSX.writeFile(workbook, "Billable_Non_Billable_List.xlsx");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox container pt={2} pb={2} px={3}>

              <MDBox sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2 }}>
                <Typography
                  sx={{
                    gridColumn: "span 2",
                    color: "textPrimary",
                    fontFamily: properties.fontFamily,
                    fontSize: "15px",
                    fontWeight: "bold"
                  }}
                >
                  Employee Billing Report
                </Typography>
                <MDBox sx={{ gridColumn: "span 2" }}>
                  <MDInput
                    value={empId}
                    onChange={(e) => setEmpId(e.target.value)}
                    name="empId"
                    label="Employee ID"
                    variant="outlined"
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                </MDBox>
                <MDBox sx={{ gridColumn: "span 2" }}>
                  <MDInput
                    value={empName}
                    onChange={(e) => setEmpName(e.target.value)}
                    name="empName"
                    label="Employee Name"
                    variant="outlined"
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                </MDBox>
                <MDBox sx={{ gridColumn: "span 2" }}>
                  <FormControl fullWidth sx={{ height: "100%" }}>
                    <InputLabel>Chargeability</InputLabel>
                    <Select
                      value={chargeability}
                      onChange={(e) => setChargeability(e.target.value)}
                      label="Chargeability"
                      fullWidth
                      sx={{ height: "100%" }}
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="1">Billable</MenuItem>
                      <MenuItem value="0">Non-Billable</MenuItem>
                    </Select>
                  </FormControl>
                </MDBox>
                <MDButton
                  sx={{ gridColumn: "span 2", background: properties.colors.bgcolorbluethem }}
                  variant="contained"
                  color="info"
                  onClick={handleSearch}
                >
                  <SearchIcon sx={{ mr: 1 }} /> Search
                </MDButton>
                <MDButton
                  sx={{ gridColumn: "span 2" }}
                  variant="gradient"
                  color="success"
                  onClick={handleDownloadExcel}
                >
                  <DownloadIcon sx={{ mr: 1 }} /> Download
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </AppBar>

        {/* DataGrid */}
        <MDBox>
          <Paper>
            <DataGrid
              rows={filteredData}
              columns={columns}
              paginationMode="server"
              rowCount={totalRows}
              pageSizeOptions={[rowLength]}
              paginationModel={{ page, pageSize }}
              loading={loading}
              components={{ Toolbar: GridToolbar }}
              hideFooter
              sx={{
                height: "450px",
                fontWeight:300,
                fontFamily: properties.fontFamily,
                fontSize: "14px",
                "& .MuiDataGrid-scrollbar": {
                  width: "10px !important",
                },
              }}
            />
            <CustomPagination
              page={page}
              pageSize={pageSize}
              totalRows={totalRows}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onGoToLastPage={handleGoToLastPage}
              loading={loading}
            />
          </Paper>
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}
